import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  FileText,
  CheckCircle2,
  ArrowRight,
  Shield,
  Zap,
  Upload,
  BookOpen,
  ScrollText,
  HeartPulse,
  Lightbulb,
  Calculator,
  TrendingUp,
  Users,
  Sparkles,
  Check,
  X,
  Star,
  BarChart3,
  Link2,
  Award,
  BadgeCheck,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import { AuthModal, type AuthModalView } from "@/components/auth/AuthModal";
import { PublicHeader } from "@/components/layout/PublicHeader";
import { subscriptionsApi } from "@/lib/api";
import { useCompanyInfo } from "@/hooks/use-company-info";
import {
  PageMeta,
  PUBLIC_PAGE_META,
  SEO_CONFIG,
  getOrganizationSchema,
  getProductSchema,
  getFAQSchema,
  getWebSiteSchema,
  getLocalBusinessSchema,
} from "@/lib/seo";
import logoSvg from "@/assets/logo.svg";

// Platform features configuration - matching Dashboard
const PLATFORM_FEATURES = [
  {
    id: "reports",
    title: "Årsredovisningar",
    subtitle: "K2 & K3",
    description: "Automatiserad årsredovisning från SIE-fil. Intelligent wizard, regelrätt resultat, Bolagsverkets format.",
    icon: FileText,
    color: "bg-blue-500",
    bgLight: "bg-blue-50",
    iconColor: "text-blue-600",
    gradient: "from-blue-500 to-blue-600",
  },
  {
    id: "sharebook",
    title: "Digital aktiebok",
    subtitle: "ABL 5 kap",
    description: "Hantera aktieägare, transaktioner och ägarhistorik med full spårbarhet och hash-kedja.",
    icon: BookOpen,
    color: "bg-emerald-500",
    bgLight: "bg-emerald-50",
    iconColor: "text-emerald-600",
    gradient: "from-emerald-500 to-emerald-600",
  },
  {
    id: "protocols",
    title: "Protokoll",
    subtitle: "Årsstämma",
    description: "Generera korrekta årsstämmoprotokoll enligt ABL 7 kap. Data hämtas automatiskt från årsredovisningen.",
    icon: ScrollText,
    color: "bg-violet-500",
    bgLight: "bg-violet-50",
    iconColor: "text-violet-600",
    gradient: "from-violet-500 to-violet-600",
  },
  {
    id: "health",
    title: "Företagshälsa",
    subtitle: "Finansiell analys",
    description: "Analysera soliditet, likviditet och nyckeltal. Jämför med branschsnitt och få varningar i tid.",
    icon: HeartPulse,
    color: "bg-rose-500",
    bgLight: "bg-rose-50",
    iconColor: "text-rose-600",
    gradient: "from-rose-500 to-rose-600",
  },
  {
    id: "advisory",
    title: "Rådgivning",
    subtitle: "AI-driven",
    description: "Få skräddarsydda rekommendationer baserat på ditt företags ekonomi och situation.",
    icon: Lightbulb,
    color: "bg-amber-500",
    bgLight: "bg-amber-50",
    iconColor: "text-amber-600",
    gradient: "from-amber-500 to-amber-600",
  },
  {
    id: "integrations",
    title: "Integrationer",
    subtitle: "Fortnox, Spiris m.fl.",
    description: "Koppla ihop med Fortnox, Spiris, Bolagsverket och andra system för automatisk datasynk.",
    icon: Link2,
    color: "bg-cyan-500",
    bgLight: "bg-cyan-50",
    iconColor: "text-cyan-600",
    gradient: "from-cyan-500 to-cyan-600",
  },
];

// Cost comparison data
const COMPARISON_ITEMS = [
  { feature: "K2-årsredovisning", traditional: true, kvanten: true },
  { feature: "K3-årsredovisning", traditional: true, kvanten: true },
  { feature: "Digital inlämning (iXBRL)", traditional: "Extra kostnad", kvanten: true },
  { feature: "Digital aktiebok", traditional: "Separat system", kvanten: true },
  { feature: "Årsstämmoprotokoll", traditional: "Extra kostnad", kvanten: true },
  { feature: "Finansiell analys & nyckeltal", traditional: false, kvanten: true },
  { feature: "AI-driven rådgivning", traditional: false, kvanten: true },
  { feature: "Obegränsade korrigeringar", traditional: "Timdebitering", kvanten: true },
  { feature: "Arkivering i 7 år", traditional: "Extra kostnad", kvanten: true },
  { feature: "Bolagsverket-koppling", traditional: false, kvanten: true },
];

// Stats for social proof - price is set dynamically in component
const getStats = (priceFormatted: string) => [
  { value: priceFormatted, label: "kr exkl. moms", suffix: "" },
  { value: "5", label: "minuter att komma igång", suffix: "min" },
  { value: "100", label: "regelrätt enligt K2/K3", suffix: "%" },
];

export function LandingPage() {
  // Auth modal for CTA buttons (separate from header's auth modal)
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalView, setAuthModalView] = useState<AuthModalView>("login");
  const [priceExclVat, setPriceExclVat] = useState<number | null>(null);
  const { company } = useCompanyInfo();

  useEffect(() => {
    subscriptionsApi.getPrice().then((res) => {
      if (res.success && res.data) {
        setPriceExclVat(res.data.priceExclVat);
      }
    }).catch(() => {
      // Fallback handled by null state
    });
  }, []);

  const openRegisterModal = () => {
    setAuthModalView("register");
    setIsAuthModalOpen(true);
  };

  const formattedPrice = priceExclVat !== null
    ? new Intl.NumberFormat("sv-SE").format(priceExclVat)
    : "2 495";

  // Generate structured data schemas for SEO
  const structuredData = [
    getOrganizationSchema(),
    getProductSchema(priceExclVat),
    getFAQSchema(),
    getWebSiteSchema(),
    getLocalBusinessSchema(),
  ];

  return (
    <>
      <PageMeta
        title={PUBLIC_PAGE_META.landing.title}
        description={PUBLIC_PAGE_META.landing.description}
        keywords={PUBLIC_PAGE_META.landing.keywords}
        canonicalUrl={SEO_CONFIG.siteUrl}
        fullTitle
      />

      {/* JSON-LD Structured Data for SEO */}
      <Helmet>
        {structuredData.map((schema, index) => (
          <script key={index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>

      <div className="min-h-screen bg-white text-gray-900">
        {/* Navigation - Unified header component */}
        <PublicHeader />

        <main>
        {/* Hero Section */}
        <section aria-labelledby="hero-heading" className="relative pt-16 pb-24 lg:pt-24 lg:pb-32 overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary-100/40 via-blue-100/30 to-emerald-100/40 rounded-full blur-3xl opacity-60" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-50 to-emerald-50 border border-primary-100 text-primary-700 rounded-full text-sm font-medium mb-8">
                <Sparkles className="w-4 h-4" />
                <span>Följer K2/K3-regelverket med senaste ändringarna</span>
              </div>

              {/* Headline */}
              <h1 id="hero-heading" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 tracking-tight leading-[1.1]">
                Allt ditt aktiebolag behöver
                <span className="block bg-gradient-to-r from-primary-600 to-emerald-600 bg-clip-text text-transparent mt-2">
                  — för en bråkdel av priset
                </span>
              </h1>

              {/* Subheadline */}
              <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
                Årsredovisning, aktiebok, protokoll, finansiell analys och rådgivning.
                <span className="font-semibold text-gray-900"> Allt ingår för {formattedPrice} kr</span> —
                jämfört med 8 000–25 000 kr hos en traditionell byrå.
              </p>

              {/* CTA Buttons */}
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={openRegisterModal}
                  className="group inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-full hover:from-primary-700 hover:to-primary-800 shadow-lg shadow-primary-500/25 transition-all duration-200 w-full sm:w-auto"
                >
                  Kom igång gratis
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </button>
                <Link
                  to="/wizard"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-gray-700 bg-white border-2 border-gray-200 rounded-full hover:border-primary-200 hover:bg-primary-50 transition-all duration-200 w-full sm:w-auto"
                >
                  <Upload className="w-5 h-5" />
                  Prova utan konto
                </Link>
              </div>

              {/* Trust indicators */}
              <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Gratis att prova hela systemet</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Inga dolda avgifter</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  <span>Bolagsverkets format</span>
                </div>
              </div>
            </div>

            {/* Stats bar */}
            <div className="mt-16 grid grid-cols-3 gap-4 max-w-2xl mx-auto">
              {getStats(formattedPrice).map((stat, index) => (
                <div key={index} className="text-center p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <div className="text-2xl sm:text-3xl font-bold text-gray-900">
                    {stat.value}{stat.suffix}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who can do their own annual report */}
        <section className="py-16 lg:py-24 bg-gradient-to-b from-emerald-50 to-white border-y border-emerald-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
                  <BadgeCheck className="w-4 h-4" />
                  Visste du?
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                  Vet du att du kan göra årsredovisningen själv?
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  Enligt svenska regler behöver de allra flesta aktiebolag <strong>ingen revisor</strong>.
                  Om ditt företag är ett "mindre företag" kan du helt lagligt upprätta årsredovisningen själv —
                  och spara tusentals kronor varje år.
                </p>

                <div className="bg-white rounded-2xl p-6 border border-emerald-200 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    Du slipper revisor om du uppfyller max 1 av dessa gränser:
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Users className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Medelantal anställda:</span>
                        <span className="text-gray-600"> högst 3</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <BarChart3 className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Balansomslutning:</span>
                        <span className="text-gray-600"> högst 1,5 miljoner kr</span>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Nettoomsättning:</span>
                        <span className="text-gray-600"> högst 3 miljoner kr</span>
                      </div>
                    </li>
                  </ul>
                  <p className="mt-4 text-sm text-gray-500">
                    Källa: Aktiebolagslagen 9 kap 1§. Gäller om bolaget underskridit gränserna de två senaste räkenskapsåren.
                  </p>
                </div>
              </div>

              <div className="relative">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-8 text-white shadow-xl">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium mb-4">
                      <Award className="w-4 h-4" />
                      Fakta
                    </div>
                    <h3 className="text-2xl font-bold">De flesta svenska aktiebolag</h3>
                    <p className="mt-2 text-emerald-100">kan upprätta sin årsredovisning utan revisor</p>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-200" />
                        <span>Kvanten säkerställer att allt blir rätt enligt K2/K3</span>
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-200" />
                        <span>Automatisk validering mot Bolagsverkets krav</span>
                      </div>
                    </div>
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-emerald-200" />
                        <span>Stöd för digital inlämning (iXBRL)</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative elements */}
                <div className="absolute -z-10 top-4 right-4 w-full h-full bg-emerald-200 rounded-3xl" />
              </div>
            </div>
          </div>
        </section>

        {/* Platform Features */}
        <section id="features" aria-labelledby="features-heading" className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                Allt ingår
              </div>
              <h2 id="features-heading" className="text-3xl sm:text-4xl font-bold text-gray-900">
                En komplett plattform för ditt företag
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Sex kraftfulla verktyg som arbetar tillsammans — allt för ett fast pris per år.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {PLATFORM_FEATURES.map((feature) => (
                <div
                  key={feature.id}
                  className="group relative bg-white rounded-2xl p-6 border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className={`w-12 h-12 ${feature.bgLight} rounded-xl flex items-center justify-center mb-4`}>
                    <feature.icon className={`w-6 h-6 ${feature.iconColor}`} />
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                      {feature.subtitle}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>

                  {/* Hover gradient line */}
                  <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradient} rounded-b-2xl opacity-0 group-hover:opacity-100 transition-opacity`} />
                </div>
              ))}
            </div>

            {/* Coming soon teaser */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm">
                <Calculator className="w-4 h-4" />
                <span>Snart: K10-deklarationer och fler skattebilagor</span>
              </div>
            </div>
          </div>
        </section>

        {/* Cost Comparison */}
        <section id="comparison" aria-labelledby="comparison-heading" className="py-20 lg:py-32 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="comparison-heading" className="text-3xl sm:text-4xl font-bold text-gray-900">
                Jämför och se skillnaden
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Traditionell redovisningsbyrå tar ofta 8 000–25 000 kr <em>bara</em> för årsredovisningen.
                Med Kvanten får du så mycket mer — till en bråkdel av kostnaden.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-200">
                {/* Header */}
                <div className="grid grid-cols-3 bg-gray-50 border-b border-gray-200">
                  <div className="p-6 font-medium text-gray-700">Funktion</div>
                  <div className="p-6 text-center border-x border-gray-200">
                    <div className="font-medium text-gray-700">Traditionell byrå</div>
                    <div className="text-sm text-gray-500 mt-1">8 000–25 000 kr/år</div>
                  </div>
                  <div className="p-6 text-center bg-primary-50">
                    <div className="font-semibold text-primary-700">Kvanten</div>
                    <div className="text-sm text-primary-600 mt-1">{formattedPrice} kr/år</div>
                  </div>
                </div>

                {/* Rows */}
                <div className="divide-y divide-gray-100">
                  {COMPARISON_ITEMS.map((item, index) => (
                    <div key={index} className="grid grid-cols-3">
                      <div className="p-4 text-gray-700 text-sm">{item.feature}</div>
                      <div className="p-4 text-center border-x border-gray-100">
                        {item.traditional === true ? (
                          <Check className="w-5 h-5 text-green-500 mx-auto" />
                        ) : item.traditional === false ? (
                          <X className="w-5 h-5 text-gray-300 mx-auto" />
                        ) : (
                          <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded">
                            {item.traditional}
                          </span>
                        )}
                      </div>
                      <div className="p-4 text-center bg-primary-50/50">
                        <Check className="w-5 h-5 text-primary-600 mx-auto" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="grid grid-cols-3 bg-gray-50 border-t border-gray-200">
                  <div className="p-6 font-semibold text-gray-900">Totalt per år</div>
                  <div className="p-6 text-center border-x border-gray-200">
                    <span className="text-2xl font-bold text-gray-400 line-through">8 000+</span>
                    <span className="text-gray-400 text-sm ml-1">kr</span>
                  </div>
                  <div className="p-6 text-center bg-primary-100">
                    <span className="text-2xl font-bold text-primary-700">{formattedPrice}</span>
                    <span className="text-primary-600 text-sm ml-1">kr</span>
                  </div>
                </div>
              </div>

              {/* Savings callout */}
              <div className="mt-8 text-center">
                <div className="inline-flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-2xl shadow-lg">
                  <Star className="w-6 h-6" />
                  <span className="font-semibold">
                    Spara upp till 20 000 kr per år — och få tillgång till mer
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 lg:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Så enkelt kommer du igång
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Från SIE-fil till komplett årsredovisning på tre enkla steg
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {/* Step 1 */}
              <div className="relative text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-500/25">
                  <Upload className="w-8 h-8 text-white" />
                </div>
                <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gray-200 hidden md:block" style={{ transform: 'translateX(50%)' }} />
                <span className="inline-flex items-center justify-center w-8 h-8 bg-primary-600 text-white rounded-full text-sm font-bold mb-4">
                  1
                </span>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Ladda upp SIE-fil
                </h3>
                <p className="text-gray-600">
                  Exportera en SIE4-fil från Fortnox, Spiris eller annat bokföringsprogram och ladda upp den.
                </p>
              </div>

              {/* Step 2 */}
              <div className="relative text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </div>
                <div className="absolute top-8 left-1/2 w-full h-0.5 bg-gray-200 hidden md:block" style={{ transform: 'translateX(50%)' }} />
                <span className="inline-flex items-center justify-center w-8 h-8 bg-emerald-600 text-white rounded-full text-sm font-bold mb-4">
                  2
                </span>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Komplettera det som saknas
                </h3>
                <p className="text-gray-600">
                  Vår smarta wizard guidar dig genom det som SIE-filen inte kan berätta — ofta bara några frågor.
                </p>
              </div>

              {/* Step 3 */}
              <div className="relative text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-violet-500 to-violet-600 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/25">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <span className="inline-flex items-center justify-center w-8 h-8 bg-violet-600 text-white rounded-full text-sm font-bold mb-4">
                  3
                </span>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Exportera och lämna in
                </h3>
                <p className="text-gray-600">
                  Ladda ner PDF och iXBRL för digital inlämning till Bolagsverket. Klart!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" aria-labelledby="pricing-heading" className="py-20 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 id="pricing-heading" className="text-3xl sm:text-4xl font-bold text-gray-900">
                Enkel och transparent prissättning
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                Allt ingår. Ett pris. Inga överraskningar.
              </p>
            </div>

            <div className="max-w-lg mx-auto">
              <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
                <div className="p-8 lg:p-10">
                  <div className="text-center mb-8">
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-6xl lg:text-7xl font-bold text-gray-900">
                        {formattedPrice}
                      </span>
                      <span className="text-xl text-gray-500">kr</span>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">exkl. moms · per företag, per år</p>

                    <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium">
                      <Zap className="w-4 h-4" />
                      Hela wizarden är gratis att prova
                    </div>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {[
                      "Automatiserad K2 & K3 årsredovisning",
                      "PDF + iXBRL för digital inlämning",
                      "Digital aktiebok enligt ABL",
                      "Årsstämmoprotokoll",
                      "Finansiell analys & nyckeltal",
                      "AI-driven rådgivning",
                      "Obegränsade korrigeringar",
                      "Arkivering i 7 år",
                      "Prioriterad support",
                    ].map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-gradient-to-br from-primary-500 to-emerald-500 flex items-center justify-center flex-shrink-0">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={openRegisterModal}
                    className="block w-full text-center py-4 px-6 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-semibold rounded-xl hover:from-primary-700 hover:to-primary-800 shadow-lg shadow-primary-500/25 transition-all duration-200"
                  >
                    Kom igång gratis
                  </button>

                  <p className="mt-4 text-center text-sm text-gray-500">
                    Ingen betalning krävs för att börja
                  </p>
                </div>
              </div>

              {/* Volume discount teaser */}
              <div className="mt-8 text-center">
                <p className="text-gray-600">
                  Redovisningsbyrå? Vi erbjuder volymrabatter.{" "}
                  <a href={`mailto:${company.email}`} className="text-primary-600 font-medium hover:underline">
                    Kontakta oss
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations preview */}
        <section className="py-16 lg:py-20 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Fungerar med de system du redan använder
              </h2>
              <p className="text-gray-500 mb-10">
                Exportera SIE4-filer från valfritt bokföringsprogram
              </p>
              <div className="flex flex-wrap items-center justify-center gap-10 lg:gap-14">
                <div className="flex flex-col items-center gap-2 group">
                  <div className="w-16 h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center p-3 group-hover:shadow-md transition-shadow">
                    <img src="/logos/integrations/fortnox.svg" alt="Fortnox" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Fortnox</span>
                </div>
                <div className="flex flex-col items-center gap-2 group">
                  <div className="w-16 h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center p-2 group-hover:shadow-md transition-shadow">
                    <img src="/logos/integrations/spiris.svg" alt="Spiris" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Spiris</span>
                </div>
                <div className="flex flex-col items-center gap-2 group">
                  <div className="w-16 h-16 bg-slate-700 rounded-xl shadow-sm flex items-center justify-center p-3 group-hover:shadow-md transition-shadow">
                    <img src="/logos/integrations/bjorn-lunden.svg" alt="Björn Lundén" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Björn Lundén</span>
                </div>
                <div className="flex flex-col items-center gap-2 group">
                  <div className="w-16 h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center p-3 group-hover:shadow-md transition-shadow">
                    <img src="/logos/integrations/bokio.png" alt="Bokio" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Bokio</span>
                </div>
                <div className="flex flex-col items-center gap-2 group">
                  <div className="w-16 h-16 bg-white rounded-xl shadow-sm border border-gray-100 flex items-center justify-center p-2 group-hover:shadow-md transition-shadow">
                    <img src="/logos/integrations/bolagsverket.svg" alt="Bolagsverket" className="w-full h-full object-contain" />
                  </div>
                  <span className="text-sm text-gray-600 font-medium">Bolagsverket</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 lg:py-24 bg-gradient-to-br from-gray-900 via-gray-900 to-gray-800 relative overflow-hidden">
          {/* Background decoration */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wMyI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Redo att spara tid och pengar?
            </h2>
            <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
              Skapa ett konto gratis och se hur enkelt det kan vara att
              göra årsredovisningen själv.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={openRegisterModal}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-gray-900 bg-white rounded-full hover:bg-gray-100 shadow-xl transition-all duration-200"
              >
                Skapa gratis konto
                <ArrowRight className="w-5 h-5" />
              </button>
              <Link
                to="/wizard"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-base font-semibold text-white border-2 border-white/30 rounded-full hover:bg-white/10 transition-all duration-200"
              >
                Prova utan konto
              </Link>
            </div>
          </div>
        </section>
        </main>

        {/* Footer */}
        <footer className="bg-gray-50 border-t border-gray-200" role="contentinfo">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            {/* Link columns - 1 col on xs, 2 on sm, 4 on md+ */}
            <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 lg:gap-12">
              {/* Column 1: Produkter */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Produkter</h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/arsredovisning"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Årsredovisning
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/arsredovisning/k2"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      K2 Årsredovisning
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/arsredovisning/k3"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      K3 Årsredovisning
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/aktiebok"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Digital aktiebok
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/priser"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Priser
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Column 2: Resurser */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Resurser</h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/arsredovisning/guider"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Guider & artiklar
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/arsredovisning/guider/forsta-arsredovisningen"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Första årsredovisningen
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/arsredovisning/guider/k2-vs-k3"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      K2 vs K3
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/arsredovisning/guider/sie-fil"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      SIE-fil guide
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/vanliga-fragor"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Vanliga frågor
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Column 3: Företag */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Företag</h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/om-oss"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Om Kvanten
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/kontakt"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Kontakt
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Column 4: Juridiskt */}
              <div>
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Juridiskt</h3>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/villkor"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Användarvillkor
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/integritetspolicy"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Integritetspolicy
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/cookies"
                      className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Cookies
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom bar */}
            <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Logo & Copyright - stack on mobile */}
                <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
                  <img src={logoSvg} alt="Kvanten" className="h-6 w-auto" />
                  <p className="text-xs sm:text-sm text-gray-500">
                    © {new Date().getFullYear()} Kvanten. Alla rättigheter förbehållna.
                  </p>
                </div>

                {/* Made in Sweden */}
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
                  <span>Utvecklat i Sverige</span>
                  <svg className="w-5 h-4" viewBox="0 0 20 16" fill="none" aria-hidden="true">
                    <rect width="20" height="16" fill="#006AA7" />
                    <rect x="6" width="2.5" height="16" fill="#FECC00" />
                    <rect y="6.5" width="20" height="3" fill="#FECC00" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </footer>

        {/* Auth Modal */}
        <AuthModal
          isOpen={isAuthModalOpen}
          onClose={() => setIsAuthModalOpen(false)}
          currentView={authModalView}
          onViewChange={setAuthModalView}
        />
      </div>
    </>
  );
}
